import React, { useMemo, useState } from 'react';
import { Alert, Box, Chip, TextField, Tooltip, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { aiAdminTokens as t } from './tokens';

const CHIP_SX = { height: 22, fontSize: 11.5, fontFamily: t.font.sans };

// Los precios vienen por millón de tokens y con hasta dos decimales; los que no
// declaran pricing muestran un guión en vez de un cero engañoso.
const price = (value) => (
    typeof value === 'number' ? `US$ ${value.toFixed(2)}` : '—'
);

export default function ModelsTab({ tr, models }) {
    const [search, setSearch] = useState('');

    const columns = useMemo(() => [
        { field: 'name', headerName: tr('name'), flex: 1.4, minWidth: 220 },
        { field: 'provider', headerName: tr('ai_admin_provider'), width: 120 },
        { field: 'model', headerName: tr('ai_admin_model_id'), flex: 1, minWidth: 170 },
        {
            field: 'input_per_1m', headerName: tr('ai_admin_price_input'), width: 120,
            align: 'right', headerAlign: 'right', valueFormatter: price,
        },
        {
            field: 'output_per_1m', headerName: tr('ai_admin_price_output'), width: 120,
            align: 'right', headerAlign: 'right', valueFormatter: price,
        },
        {
            field: 'cached_input_per_1m', headerName: tr('ai_admin_price_cached'), width: 130,
            align: 'right', headerAlign: 'right', valueFormatter: price,
        },
        {
            field: 'pricing_updated_at', headerName: tr('ai_admin_pricing_updated'), width: 130,
            sortable: false,
            valueFormatter: (value) => value || '—',
        },
        {
            field: 'credentials_available',
            headerName: tr('ai_admin_credentials'),
            width: 130,
            sortable: false,
            renderCell: (params) => (params.value ? (
                <Chip label="OK" size="small"
                      sx={{ ...CHIP_SX, bgcolor: t.color.okSoft, color: t.color.ok }}/>
            ) : (
                <Chip label={tr('ai_admin_no_credentials')} size="small"
                      sx={{ ...CHIP_SX, bgcolor: t.color.warnSoft, color: t.color.warn }}/>
            )),
        },
        {
            field: 'params',
            headerName: 'Params',
            flex: 1,
            minWidth: 160,
            sortable: false,
            // La forma de params varía por proveedor y no está normalizada, así
            // que se muestra el JSON crudo y el detalle va al tooltip.
            renderCell: (params) => {
                const text = JSON.stringify(params.value ?? {});
                return (
                    <Tooltip title={<pre style={{ margin: 0 }}>{JSON.stringify(params.value ?? {}, null, 2)}</pre>}>
                        <Box component="span" sx={{
                            fontFamily: 'ui-monospace, monospace', fontSize: 11.5,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                            {text === '{}' ? '—' : text}
                        </Box>
                    </Tooltip>
                );
            },
        },
    ], [tr]);

    const rows = useMemo(() => {
        const needle = search.trim().toLowerCase();
        return (models.data?.models ?? [])
            .filter((m) => !needle
                || `${m.name} ${m.provider ?? ''} ${m.model}`.toLowerCase().includes(needle))
            .map((m) => ({
                id: m.name,
                name: m.name,
                provider: m.provider ?? '—',
                model: m.model,
                params: m.params,
                credentials_available: m.credentials_available,
                input_per_1m: m.pricing?.input_per_1m,
                output_per_1m: m.pricing?.output_per_1m,
                cached_input_per_1m: m.pricing?.cached_input_per_1m,
                pricing_updated_at: m.pricing?.updated_at,
            }));
    }, [models.data, search]);

    if (models.error) {
        return (
            <Alert severity="error">
                {typeof models.error === 'string' ? models.error : tr('ai_admin_service_down')}
            </Alert>
        );
    }

    return (
        <Box>
            <Typography sx={{ fontFamily: t.font.sans, fontSize: 12.5, mb: 1.5 }}>
                {tr('ai_admin_pricing_note')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
                <TextField
                    size="small"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={tr('ai_admin_search_models')}
                    sx={{ minWidth: 260, bgcolor: t.color.surface }}
                />
                <Typography sx={{ fontFamily: t.font.sans, fontSize: 12.5, ml: 'auto' }}>
                    {`${rows.length} ${tr('ai_admin_models').toLowerCase()}`}
                </Typography>
            </Box>
            <DataGrid
                rows={rows}
                columns={columns}
                loading={models.loading}
                autoHeight
                disableRowSelectionOnClick
                hideFooterSelectedRowCount
                initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                pageSizeOptions={[25, 50, 100]}
                localeText={{
                    MuiTablePagination: { labelRowsPerPage: tr('rows_per_page') },
                    noRowsLabel: tr('ai_admin_no_models'),
                }}
                sx={{
                    bgcolor: t.color.surface,
                    border: `1px solid ${t.color.border}`,
                    borderRadius: `${t.radius.lg}px`,
                    fontFamily: t.font.sans,
                    '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 600, fontSize: 12.5 },
                    '& .MuiDataGrid-cell': { fontSize: 13 },
                }}
            />
        </Box>
    );
}
